// 2026-10-04 addition, revised same day — see ADMIN_RCM_CONSOLIDATION_PLAN.md.
// Patient-side RCM table: filters, payment status, receipt link, resend-
// payment-link action. Separate component from RcmClinicianPanel per
// explicit instruction. Revision adds what the first pass was missing,
// flagged directly: a per-field (i) tooltip explaining what to enter (same
// convention as RequisitionConversion.js's FieldLabel), and real inline
// validation on DOB/month/year instead of silently passing through
// whatever's typed.
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, TextField, Select, MenuItem, Button, Chip, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { formatEST } from "../dateUtils";
import { extractErrorDetail } from "./validators";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const B = { navy: "#00357a", green: "#1a6b3a", gold: "#c9a96e", red: "#c0392b" };

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

const STATUS_CHIP = {
  completed: { label: "Completed", color: "#1a6b3a", bg: "rgba(26,107,58,0.1)" },
  pending:   { label: "Pending",   color: "#8a6d1f", bg: "rgba(201,169,110,0.18)" },
  failed:    { label: "Failed",    color: "#c0392b", bg: "rgba(192,57,43,0.1)" },
};

// Proove-parity build, Part 5 — per-panel vocabulary (paid/pending/unpaid),
// deliberately kept separate from STATUS_CHIP above: these are two
// different billing concepts (one flat per-account copay vs. N
// independent per-item panel requests), same distinction already enforced
// on the patient-facing side (MyPayments.js's own PANEL_STATUS_LABEL/COLOR
// vs. its copay-only maps).
const PANEL_STATUS_CHIP = {
  paid:     { label: "Paid",     color: "#1a6b3a", bg: "rgba(26,107,58,0.1)" },
  pending:  { label: "Pending",  color: "#8a6d1f", bg: "rgba(201,169,110,0.18)" },
  unpaid:   { label: "Unpaid",   color: "#64748b", bg: "rgba(100,116,139,0.1)" },
  // 2026-10-18 fix (real gap) — a canceled row (most relevantly a
  // canceled subscription) used to collapse into the same "Unpaid" chip
  // as something never attempted at all, hiding the one state admin most
  // needs to distinguish for a subscription specifically.
  canceled: { label: "Canceled", color: "#b45309", bg: "rgba(180,83,9,0.12)" },
  // 2026-10-18 fix (downstream-mismatch-checker finding) — a row flagged
  // identity_mismatch on portal's side used to either show "Paid" (before
  // today) or, briefly, "Unpaid" with a resend icon that would then get
  // rejected by portal's own already-paid guard. Neither is right — this
  // is its own distinct state: don't trust this row's status as this
  // patient's own until someone manually confirms the match.
  review_needed: { label: "Needs Review", color: "#9333ea", bg: "rgba(147,51,234,0.12)" },
};
// Short display names for the panel chips — the table column is narrow;
// full PAYMENT_ITEMS labels ("Proove Drug Metabolism (PGx) Profile") would
// make every row wrap badly. Tooltip on each chip shows the full label.
const PANEL_SHORT_LABEL = {
  copay: "Copay",
  opioid_risk: "Opioid Risk", pain_perception: "Pain Perception",
  drug_metabolism: "Drug Metab.", all_panels: "All Panels",
  monthly_ai_subscription: "Monthly Sub.",
};

// 2026-10-05 addition (plan A.7) — payment_preference was shown as the raw
// DB value ("now"/"later") with only textTransform:"capitalize" applied;
// the internal registration-alert email already maps this correctly
// ("Pay now"/"Pay later") — reusing that same label here.
const PAYMENT_PREFERENCE_LABEL = { now: "Pay now", later: "Pay later" };

// ── Field definitions, each with real validation + help text ────────────────
// 2026-10-04 revision — Month/Year were free-text fields (placeholder
// "1-12"/"2026"), flagged directly as looking unfinished/error-prone.
// Real dropdowns now, so there's no longer a malformed-value case for
// either — only DOB (still free text, since it's a specific date) needs
// format validation.
const DOB_RE = /^\d{4}-\d{2}-\d{2}$/;

const MONTH_OPTIONS = [
  { value: "1", label: "January" }, { value: "2", label: "February" }, { value: "3", label: "March" },
  { value: "4", label: "April" }, { value: "5", label: "May" }, { value: "6", label: "June" },
  { value: "7", label: "July" }, { value: "8", label: "August" }, { value: "9", label: "September" },
  { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" },
];
// Current year back to when the app could plausibly have its earliest
// registrations (2026, the year this app shipped) — extends forward one
// year too, so it doesn't go stale the moment the calendar turns over.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2026 + 2 }, (_, i) => CURRENT_YEAR + 1 - i);

function validateField(key, value) {
  if (!value) return "";
  if (key === "dob" && !DOB_RE.test(value)) return "Use YYYY-MM-DD";
  return "";
}

function FieldWithHelp({ label, help, error, children }) {
  return (
    <Box sx={{ minWidth: 150 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: error ? B.red : "text.secondary", display: "flex", alignItems: "center", gap: 0.4, mb: 0.4 }}>
        {label}
        <Tooltip title={help} arrow placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 13.5, color: "text.disabled", cursor: "help" }} />
        </Tooltip>
      </Typography>
      {children}
      {error && <Typography sx={{ fontSize: 12, color: B.red, mt: 0.2 }}>{error}</Typography>}
    </Box>
  );
}

function formatAmount(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function RcmPatientPanel({ isDark }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ email: "", phone: "", first_name: "", last_name: "", dob: "", payment_status: "", month: "", year: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const [resendMsg, setResendMsg] = useState("");

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: validateField(key, value) }));
  };

  const hasErrors = Object.values(fieldErrors).some(Boolean);

  const fetchPatients = useCallback(async () => {
    if (hasErrors) return;
    setLoading(true); setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await axios.get(`${API}/api/admin/rcm/patients`, { params, headers: authHeaders() });
      // 2026-10-04 fix — real bug, confirmed directly: the backend returns
      // 200 OK with {patients: [], error: "PORTAL_MONGO_URI not
      // configured"} when the cross-service connection to the AI portal's
      // database isn't set up — a real, actionable reason "nothing is
      // visible," silently discarded before this fix since it's not a
      // thrown HTTP error, just an empty-looking success response.
      if (res.data.error) {
        setError(res.data.error);
        setPatients([]);
      } else {
        setPatients(res.data.patients || []);
      }
    } catch (err) {
      // 2026-10-07 fix — every other admin_token-gated view in this app
      // (AuditDashboard, UserDashboard, AdminDashboard) bounces to
      // /admin-login on a 401; this one was the lone exception, leaving an
      // expired admin stuck on a dead page with a generic error.
      if (err?.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin-login"); return; }
      setError(extractErrorDetail(err, "Failed to load patients."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { fetchPatients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = async (id) => {
    setResendingId(id); setResendMsg("");
    try {
      await axios.post(`${API}/api/admin/rcm/resend-payment-link`, { user_id: id }, { headers: authHeaders() });
      setResendMsg("Payment link resent.");
    } catch (err) {
      setResendMsg(extractErrorDetail(err, "Resend failed."));
    } finally {
      setResendingId(null);
    }
  };

  // Proove-parity build, Part 5 — per-panel resend, mirrors handleResend
  // above but keyed by (patient id, item_key) so resending one patient's
  // panel link doesn't visually disable every other patient's buttons —
  // same re-entry-guard-per-key discipline as MyPayments.js's own
  // handlePay (payingKey !== itemKey check).
  // 2026-10-15 fix (ui-ux-checker finding, real bug) — this used to be a
  // single string + a global `if (resendingPanelKey !== null) return`
  // guard, so resending ONE patient's ONE panel visually dimmed every
  // OTHER patient's (and panel's) resend icons too, despite a comment
  // here claiming per-key scoping. A Set actually allows concurrent
  // in-flight resends across different keys, each one guarded only
  // against ITS OWN re-entry — the real per-key discipline this file's
  // own sibling `resendingId === p.id` pattern already has (that one just
  // never needed a Set, since there's only one resend button per row).
  const [resendingPanelKeys, setResendingPanelKeys] = useState(new Set());
  const handleResendPanel = async (id, itemKey) => {
    const key = `${id}:${itemKey}`;
    if (resendingPanelKeys.has(key)) return;
    setResendingPanelKeys((prev) => new Set(prev).add(key));
    setResendMsg("");
    try {
      await axios.post(`${API}/api/admin/rcm/resend-panel-payment-link`, { user_id: id, item_key: itemKey }, { headers: authHeaders() });
      setResendMsg("Panel payment link resent.");
    } catch (err) {
      setResendMsg(extractErrorDetail(err, "Resend failed."));
    } finally {
      setResendingPanelKeys((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const exportCsv = () => {
    const headers = ["First Name", "Last Name", "Email", "Phone", "DOB", "Payment Status", "Preference", "Amount", "Paid At"];
    const rows = patients.map((p) => [
      p.first_name, p.last_name, p.email, p.phone, p.date_of_birth,
      p.payment_status, PAYMENT_PREFERENCE_LABEL[p.payment_preference] || p.payment_preference, formatAmount(p.payment_amount_cents), p.payment_confirmed_at,
    ]);
    // Neutralize CSV formula injection — patient-controlled fields
    // (name/email from self-registration) could otherwise start with
    // =/+/-/@ and execute as a formula when staff open this in Excel/Sheets.
    const csvSafe = (v) => {
      const s = String(v ?? "");
      return /^[=+\-@]/.test(s) ? `'${s}` : s;
    };
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${csvSafe(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `patients_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.82rem", bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff" } };

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2.5, alignItems: "flex-start" }}>
        <FieldWithHelp label="Email" help="Matches anywhere in the patient's email address — partial matches OK.">
          <TextField size="small" value={filters.email} onChange={(e) => updateFilter("email", e.target.value)} sx={{ ...fieldSx, minWidth: 180 }} />
        </FieldWithHelp>
        <FieldWithHelp label="Phone" help="Digits only are compared — formatting (dashes, spaces, parens) doesn't matter.">
          <TextField size="small" value={filters.phone} onChange={(e) => updateFilter("phone", e.target.value)} sx={{ ...fieldSx, minWidth: 140 }} />
        </FieldWithHelp>
        <FieldWithHelp label="First Name" help="Partial, case-insensitive match.">
          <TextField size="small" value={filters.first_name} onChange={(e) => updateFilter("first_name", e.target.value)} sx={{ ...fieldSx, minWidth: 130 }} />
        </FieldWithHelp>
        <FieldWithHelp label="Last Name" help="Partial, case-insensitive match.">
          <TextField size="small" value={filters.last_name} onChange={(e) => updateFilter("last_name", e.target.value)} sx={{ ...fieldSx, minWidth: 130 }} />
        </FieldWithHelp>
        <FieldWithHelp label="Date of Birth" help="Exact match only — format as YYYY-MM-DD, e.g. 1990-05-12." error={fieldErrors.dob}>
          <TextField size="small" placeholder="YYYY-MM-DD" value={filters.dob} onChange={(e) => updateFilter("dob", e.target.value)}
            error={!!fieldErrors.dob} sx={{ ...fieldSx, minWidth: 140 }} />
        </FieldWithHelp>
        <FieldWithHelp label="Payment Status" help="Completed = paid. Pending = link sent, not yet paid. Failed = checkout expired or was declined.">
          <Select size="small" displayEmpty value={filters.payment_status} onChange={(e) => updateFilter("payment_status", e.target.value)} sx={{ ...fieldSx, minWidth: 150 }}>
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FieldWithHelp>
        <FieldWithHelp label="Month" help="Filters by registration month — combine with Year for a specific month.">
          <Select size="small" displayEmpty value={filters.month} onChange={(e) => updateFilter("month", e.target.value)} sx={{ ...fieldSx, minWidth: 140 }}>
            <MenuItem value="">Any month</MenuItem>
            {MONTH_OPTIONS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </Select>
        </FieldWithHelp>
        <FieldWithHelp label="Year" help="Filters by registration year.">
          <Select size="small" displayEmpty value={filters.year} onChange={(e) => updateFilter("year", e.target.value)} sx={{ ...fieldSx, minWidth: 110 }}>
            <MenuItem value="">Any year</MenuItem>
            {YEAR_OPTIONS.map((y) => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
          </Select>
        </FieldWithHelp>
        <Box sx={{ display: "flex", gap: 1, alignSelf: "flex-end", pb: fieldErrors && Object.values(fieldErrors).some(Boolean) ? 0 : 0 }}>
          <Button variant="contained" size="small" onClick={fetchPatients} disabled={loading || hasErrors}
            sx={{ bgcolor: B.navy, textTransform: "none", fontWeight: 600, height: 40 }}>
            {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Search"}
          </Button>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={exportCsv} disabled={!patients.length}
            sx={{ borderColor: B.navy, color: B.navy, textTransform: "none", fontWeight: 600, height: 40 }}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {error && <Typography sx={{ color: B.red, fontSize: 14.5, mb: 1.5 }}>{error}</Typography>}
      {resendMsg && <Typography sx={{ color: B.green, fontSize: 14.5, mb: 1.5 }}>{resendMsg}</Typography>}

      <Box sx={{ overflowX: "auto", borderRadius: "12px", border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,53,122,0.1)"}` }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc" }}>
              {["Name", "Email", "Phone", "DOB", "Status", "Preference", "Amount", "Paid At", "Lab Panels", "Actions"].map((h) => (
                <TableCell key={h} sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {patients.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: "text.disabled", fontSize: 14.5 }}>No patients match these filters.</TableCell></TableRow>
            ) : patients.map((p) => {
              const chip = STATUS_CHIP[p.payment_status] || STATUS_CHIP.pending;
              return (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontSize: 14 }}>{p.first_name} {p.last_name}</TableCell>
                  <TableCell sx={{ fontSize: 13.5, fontFamily: "monospace" }}>{p.email}</TableCell>
                  <TableCell sx={{ fontSize: 13.5 }}>{p.phone || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 13.5 }}>{p.date_of_birth || "—"}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 700, fontSize: 12 }} />
                      {/* 2026-10-18 fix (frontend-backend contract sweep) —
                          backend returns payment_failed_at/payment_failed_reason
                          on this row but no admin surface ever rendered them. */}
                      {p.payment_failed_reason && (
                        <Tooltip title={`Payment failed: ${p.payment_failed_reason}${p.payment_failed_at ? ` at ${formatEST(p.payment_failed_at)}` : ""}`}>
                          <Chip size="small" label="Issue" variant="outlined" sx={{ fontSize: 11, color: "#b91c1c", borderColor: "#b91c1c" }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13.5 }}>{PAYMENT_PREFERENCE_LABEL[p.payment_preference] || p.payment_preference || "—"}</TableCell>
                  <TableCell sx={{ fontSize: 13.5 }}>{formatAmount(p.payment_amount_cents)}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>
                    {formatEST(p.payment_confirmed_at, { dateOnly: true })}
                  </TableCell>
                  <TableCell>
                    {/* Proove-parity build, Part 5 — per-panel breakdown.
                        An empty array (not yet enriched, or the patient's
                        identity hashes couldn't be resolved — same fail-
                        safe-to-"show nothing" discipline as /my-payment-
                        items on the patient-facing side) shows a dash with
                        a clarifying tooltip, never a misleading "no panels
                        ordered" claim (2026-10-15 fix, ui-ux-checker
                        finding — the bare dash alone read as "nothing
                        owed" when the real cause is usually an
                        unresolved identity hash, an account-data gap). */}
                    {p.panels && p.panels.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 220 }}>
                        {p.panels.map((panel) => {
                          const pchip = PANEL_STATUS_CHIP[panel.status] || PANEL_STATUS_CHIP.unpaid;
                          const panelKey = `${p.id}:${panel.item_key}`;
                          const isResending = resendingPanelKeys.has(panelKey);
                          // 2026-10-18 fix (real gap) — a pending/unpaid chip
                          // used to look identical whether a link was sent
                          // yesterday or never sent at all. link_sent_at now
                          // carries that timestamp for every status (the
                          // backend reports checkout_started_at/created_at,
                          // whichever the row actually has).
                          //
                          // Branches on STATUS, not just whether a timestamp
                          // is present (downstream-mismatch-checker finding)
                          // — a genuinely unpaid item (no row ever existed)
                          // is the only case that should ever claim "no link
                          // ever sent". A pending/canceled row with a missing
                          // timestamp is a legacy row that predates this
                          // field being tracked, not proof nothing was sent
                          // — it must say so is unknown, never claim the
                          // opposite of what its own status implies.
                          const tooltipDate = panel.status === "paid"
                            ? (panel.payment_confirmed_at ? ` — paid ${formatEST(panel.payment_confirmed_at, { dateOnly: true })}` : "")
                            : panel.status === "unpaid"
                            ? (panel.link_sent_at ? ` — link sent ${formatEST(panel.link_sent_at, { dateOnly: true })}` : " — no link ever sent")
                            : (panel.link_sent_at ? ` — link sent ${formatEST(panel.link_sent_at, { dateOnly: true })}` : " — link sent (date unknown)");
                          return (
                            <Tooltip key={panel.item_key} title={`${panel.label} — ${pchip.label}${tooltipDate}`}>
                              <Chip
                                size="small"
                                label={
                                  // 2026-10-15 fix (downstream-mismatch-checker finding, real
                                  // bug) — "unpaid" means no patient_payment_requests row has
                                  // EVER existed for this item (nothing was ever sent); the
                                  // backend's resend endpoint 404s on that ("No payment request
                                  // on file for this item"), so showing a resend icon there just
                                  // guarantees staff hit an error. Only "pending" (a row exists,
                                  // sent but not yet paid) is actually resendable.
                                  //
                                  // 2026-10-18 fix (real gap, confirmed against portal's own
                                  // resend_payment_link) — a recurring item (monthly_ai_
                                  // subscription) has no static Stripe payment-link env var at
                                  // all, since it's a genuinely different checkout flow. The
                                  // backend would 503 with a misleading "contact engineering"
                                  // error on every click, so the icon is hidden for it here
                                  // instead of letting staff hit that dead end.
                                  panel.status === "pending" && !panel.recurring ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                                      {PANEL_SHORT_LABEL[panel.item_key] || panel.label}
                                      {/* 2026-10-15 fix (ui-ux-checker findings) — swapped the
                                          bare clickable Box for an IconButton: gets real keyboard
                                          focus/Enter-Space activation and a visible hover state for
                                          free (a role="button" span had neither — a real a11y gap),
                                          and no longer dims every OTHER patient's/panel's icon just
                                          because ONE resend anywhere is in flight (now keyed per
                                          panelKey via a Set, see handleResendPanel above). */}
                                      <Tooltip title={`Resend payment link for ${panel.label}`}>
                                        <span>
                                          <IconButton
                                            size="small"
                                            aria-label={`Resend payment link for ${panel.label}`}
                                            onClick={(e) => { e.stopPropagation(); handleResendPanel(p.id, panel.item_key); }}
                                            disabled={isResending}
                                            sx={{ p: 0.3 }}
                                          >
                                            {isResending ? <CircularProgress size={11} /> : <SendIcon sx={{ fontSize: 13 }} />}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Box>
                                  ) : (PANEL_SHORT_LABEL[panel.item_key] || panel.label)
                                }
                                sx={{ bgcolor: pchip.bg, color: pchip.color, fontWeight: 600, fontSize: 11, height: 22 }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                    ) : (
                      <Tooltip title="Panel data unavailable — usually means this patient's account is missing a date of birth or address, not that nothing is owed.">
                        <Typography sx={{ fontSize: 13, color: "text.disabled", cursor: "help" }}>—</Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {p.payment_receipt_url && (
                        <Tooltip title="View receipt">
                          <Button size="small" href={p.payment_receipt_url} target="_blank" rel="noopener noreferrer"
                            sx={{ minWidth: 0, p: 0.6, color: B.navy }}>
                            <ReceiptLongIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                      )}
                      {p.payment_status !== "completed" && (
                        <Tooltip title="Resend payment link">
                          <span>
                            <Button size="small" onClick={() => handleResend(p.id)} disabled={resendingId === p.id}
                              sx={{ minWidth: 0, p: 0.6, color: B.gold }}>
                              {resendingId === p.id ? <CircularProgress size={14} /> : <SendIcon fontSize="small" />}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
